import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import { FETCH_CART } from "../graphql/queries";
import { toast } from "react-toastify";
import { useAppBridge, safeNavigate } from "../Components/Shopify/ShopifyAppBridgeProvider";

const CheckoutPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const store = searchParams.get("store");
  const { isEmbedded } = useAppBridge();

  const [fetchCart, { data, loading }] = useLazyQuery(FETCH_CART);

  const [customerInfo, setCustomerInfo] = useState({
    email: "",
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    province: "",
    country: "",
    zip: "",
    phone: "",
  });

  const [processingOrder, setProcessingOrder] = useState(false);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const storeItems = data?.fetchCart?.lines?.filter(
    (line: any) => line.merchandise.shop_domain === store
  ) || [];

  const totalAmount = storeItems.reduce(
    (sum: number, line: any) =>
      sum + parseFloat(line.merchandise.price) * line.quantity,
    0
  );

  const handleInputChange = (field: string, value: string) => {
    setCustomerInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompleteCheckout = async () => {
    // Validate customer information
    if (!customerInfo.email || !customerInfo.firstName || !customerInfo.lastName) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setProcessingOrder(true);

    try {
      // Get the Shopify checkout URL from the cart
      const checkoutUrl = storeItems[0]?.merchandise?.checkoutUrl;

      if (!checkoutUrl) {
        toast.error("Checkout URL is missing.");
        setProcessingOrder(false);
        return;
      }

      // Redirect to Shopify's hosted checkout with customer information pre-filled
      // Note: Shopify's checkout will handle payment processing
      const checkoutWithParams = new URL(checkoutUrl);
      checkoutWithParams.searchParams.set("checkout[email]", customerInfo.email);
      checkoutWithParams.searchParams.set("checkout[shipping_address][first_name]", customerInfo.firstName);
      checkoutWithParams.searchParams.set("checkout[shipping_address][last_name]", customerInfo.lastName);
      checkoutWithParams.searchParams.set("checkout[shipping_address][address1]", customerInfo.address);
      checkoutWithParams.searchParams.set("checkout[shipping_address][city]", customerInfo.city);
      checkoutWithParams.searchParams.set("checkout[shipping_address][province]", customerInfo.province);
      checkoutWithParams.searchParams.set("checkout[shipping_address][country]", customerInfo.country);
      checkoutWithParams.searchParams.set("checkout[shipping_address][zip]", customerInfo.zip);
      checkoutWithParams.searchParams.set("checkout[shipping_address][phone]", customerInfo.phone);

      const finalCheckoutUrl = checkoutWithParams.toString();

      // Use safe navigation helper that handles embedded context and fallbacks
      safeNavigate(finalCheckoutUrl, isEmbedded);
    } catch (error) {
      console.error("Error processing checkout:", error);
      toast.error("Failed to process checkout. Please try again.");
      setProcessingOrder(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!store || storeItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h5">No items found for checkout</Typography>
        <Button variant="contained" onClick={() => navigate("/cart")} sx={{ mt: 2 }}>
          Return to Cart
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Checkout
      </Typography>

      <Grid container spacing={4}>
        {/* Customer Information Form */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>

            <TextField
              fullWidth
              label="Email"
              type="email"
              required
              value={customerInfo.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              margin="normal"
            />

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  required
                  value={customerInfo.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  required
                  value={customerInfo.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Shipping Address
            </Typography>

            <TextField
              fullWidth
              label="Address"
              value={customerInfo.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              margin="normal"
            />

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={customerInfo.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Province/State"
                  value={customerInfo.province}
                  onChange={(e) => handleInputChange("province", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={customerInfo.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={customerInfo.zip}
                  onChange={(e) => handleInputChange("zip", e.target.value)}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Phone"
              type="tel"
              value={customerInfo.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              margin="normal"
            />
          </Paper>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, position: "sticky", top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>

            <Divider sx={{ my: 2 }} />

            {storeItems.map((line: any) => (
              <Card key={line.id} sx={{ mb: 2, display: "flex" }}>
                <CardMedia
                  component="img"
                  sx={{ width: 80, height: 80, objectFit: "cover" }}
                  image={line.merchandise.image?.src || ""}
                  alt={line.merchandise.product.title}
                />
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">
                    {line.merchandise.product.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {line.merchandise.title}
                  </Typography>
                  <Typography variant="body2">
                    Qty: {line.quantity} × ${parseFloat(line.merchandise.price).toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            ))}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1">${totalAmount.toFixed(2)}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Shipping:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Calculated at next step
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6">${totalAmount.toFixed(2)}</Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={handleCompleteCheckout}
              disabled={processingOrder}
            >
              {processingOrder ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Continue to Payment"
              )}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => navigate("/cart")}
            >
              Back to Cart
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              You will be redirected to {store} for secure payment processing.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;
